import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Asset, BrandConfig, CloneDetail } from '@api/api';
import { endpoints, qk } from '../../api/endpoints';
import { CopyButton, ErrorState, RelTime, SectionCard, SkeletonRows } from '../../components/common';
import { BrandForm } from '../../components/forms';
import { LoadingButton } from '../../components/LoadingButton';
import { useNotify } from '../../components/Notify';
import { EMPTY_BRAND, brandErrors } from '../../lib/validate';
import { fmtBytes } from '../../lib/format';
import { MONO } from '../../theme';
import { useIsOwner } from '../../auth/AuthProvider';

export const ASSET_KINDS: { kind: string; label: string; help: string; accept: string }[] = [
  { kind: 'logo', label: 'Logo', help: 'SVG, used in the app header and sign-in', accept: 'image/svg+xml,.svg' },
  { kind: 'auth-hero', label: 'Sign-in hero', help: 'PNG, the picture on the sign-in screen', accept: 'image/png' },
  { kind: 'auth-hero-bg', label: 'Sign-in background', help: 'PNG, behind the hero', accept: 'image/png' },
  { kind: 'firebase-ios', label: 'Firebase iOS', help: 'GoogleService-Info.plist; BUNDLE_ID must be the rider bundle ID', accept: '.plist,application/xml,application/x-plist' },
  { kind: 'firebase-android', label: 'Firebase Android', help: 'google-services.json with a client for the Android package', accept: '.json,application/json' },
];

export function buildCommand(slug: string, app: 'rider' | 'staff', testflight = true) {
  return `tools/build-app.sh ${slug} ${app}${testflight ? ' --testflight' : ''}`;
}

function defaults(c: CloneDetail): BrandConfig {
  const base = c.domain.split('.').reverse().join('.');
  return { ...EMPTY_BRAND, appName: c.name, riderBundleId: base, staffBundleId: `${base}.staff`, androidPackage: base.replace(/-/g, '_') };
}

async function download(url: string, fallbackName: string) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    let msg = `Download failed (${res.status})`;
    try {
      const j = (await res.json()) as { error?: { message?: string; problems?: string[] }; problems?: string[] };
      const problems = j.error?.problems ?? j.problems;
      msg = problems?.length ? `The brand is not complete: ${problems.join('; ')}` : j.error?.message ?? msg;
    } catch {
      /* not JSON */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('content-disposition') ?? '';
  const name = /filename="?([^";]+)"?/.exec(cd)?.[1] ?? fallbackName;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

function AssetRow({ c, kind, asset, owner }: { c: CloneDetail; kind: (typeof ASSET_KINDS)[number]; asset?: Asset; owner: boolean }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const up = useMutation({
    mutationFn: (file: File) => endpoints.uploadAsset(c.id, kind.kind, file),
    onSuccess: () => (void qc.invalidateQueries({ queryKey: qk.assets(c.id) }), notify.ok(`${kind.label} uploaded`)),
    onError: (e) => notify.error(e),
  });
  const del = useMutation({
    mutationFn: () => endpoints.deleteAsset(c.id, asset!.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.assets(c.id) }),
    onError: (e) => notify.error(e),
  });
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {kind.label}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {asset ? (
            <>
              {asset.filename} · {fmtBytes(asset.size)} · <RelTime iso={asset.createdAt} />
            </>
          ) : (
            kind.help
          )}
        </Typography>
      </Box>
      {owner && (
        <>
          <Button component="label" size="small" variant={asset ? 'text' : 'outlined'} startIcon={<UploadFileOutlinedIcon />} disabled={up.isPending}>
            {up.isPending ? 'Uploading…' : asset ? 'Replace' : 'Upload'}
            <input hidden type="file" accept={kind.accept} onChange={(e) => e.target.files?.[0] && up.mutate(e.target.files[0])} />
          </Button>
          {asset && (
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => del.mutate()} aria-label={`Remove ${kind.label}`}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </>
      )}
    </Stack>
  );
}

export function BrandTab({ clone: c }: { clone: CloneDetail }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const owner = useIsOwner();
  const bq = useQuery({ queryKey: qk.brand(c.id), queryFn: () => endpoints.brand(c.id) });
  const aq = useQuery({ queryKey: qk.assets(c.id), queryFn: () => endpoints.assets(c.id) });
  const [b, setB] = useState<BrandConfig>(() => defaults(c));
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    if (bq.data) setB(bq.data);
  }, [bq.data]);
  const errors = brandErrors(b);
  const save = useMutation({
    mutationFn: () => endpoints.putBrand(c.id, b),
    onSuccess: (d) => (qc.setQueryData(qk.brand(c.id), d), notify.ok('Brand saved')),
    onError: (e) => notify.error(e),
  });

  if (bq.isLoading) return <SkeletonRows rows={6} height={48} />;
  if (bq.isError) return <ErrorState error={bq.error} onRetry={() => void bq.refetch()} />;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} lg={7}>
        <Stack spacing={2}>
          {!bq.data && <Alert severity="info">No brand saved yet. The fields are pre-filled from the clone name and domain.</Alert>}
          <SectionCard title="Brand config">
            <BrandForm value={b} onChange={setB} errors={errors} />
            {owner && (
              <Box sx={{ mt: 2 }}>
                <LoadingButton variant="contained" loading={save.isPending} disabled={Object.keys(errors).length > 0} onClick={() => save.mutate()}>
                  Save brand
                </LoadingButton>
              </Box>
            )}
          </SectionCard>
        </Stack>
      </Grid>
      <Grid item xs={12} lg={5}>
        <Stack spacing={2}>
          <SectionCard title="Assets">
            {aq.isError ? <ErrorState error={aq.error} /> : ASSET_KINDS.map((k) => <AssetRow key={k.kind} c={c} kind={k} owner={owner} asset={aq.data?.find((a) => a.kind === k.kind)} />)}
          </SectionCard>
          <SectionCard title="Build on the Mac">
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                The brand package holds brand.json, assets and Firebase files for the rider and staff apps. The build script downloads it itself with CONTROL_URL and CONTROL_TOKEN (Account → API tokens) set in the shell.
              </Typography>
              <Box>
                <LoadingButton
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  loading={downloading}
                  disabled={!bq.data}
                  onClick={async () => {
                    setDownloading(true);
                    try {
                      await download(endpoints.brandPackageUrl(c.id), `brand-${c.slug}.zip`);
                    } catch (e) {
                      notify.error(e);
                    } finally {
                      setDownloading(false);
                    }
                  }}
                >
                  Download brand package
                </LoadingButton>
              </Box>
              {(['rider', 'staff'] as const).map((app) => (
                <Stack key={app} direction="row" alignItems="center" sx={{ bgcolor: 'action.hover', borderRadius: 1, pl: 1.5 }}>
                  <Typography sx={{ fontFamily: MONO, fontSize: 13, flex: 1, wordBreak: 'break-all' }} data-testid={`build-cmd-${app}`}>
                    {buildCommand(c.slug, app)}
                  </Typography>
                  <CopyButton text={buildCommand(c.slug, app)} label="Copy command" />
                </Stack>
              ))}
              <Typography variant="caption" color="text.secondary">
                Run in the sharing-control checkout on the Mac. It unpacks the package into the local X123 monorepo, bumps the build number, archives and uploads to TestFlight.
              </Typography>
            </Stack>
          </SectionCard>
        </Stack>
      </Grid>
    </Grid>
  );
}
